/**
 * Print Cover chapter — A4 full-bleed poster matching Canva page-01.
 * Brand bars only; meta / theme / contract live on Overview and Letter.
 */

import React from "react";
import type { MonthlyConsultationBundle } from "../../../../utils/forecast/monthlyConsultation";
import coverBackground from "../../../../assets/monthly-consultation/pdf/cover-background.png";
import { PdfChapterShell } from "../atoms";
import { monthPosterLabel } from "../pdfCopy";
import {
  cardWhite,
  charcoal,
  coverBarGradient,
  coverCondensedFontFamily,
  cream,
} from "../pdfTheme";

/**
 * Props for the PDF cover chapter.
 */
export interface PdfChapterCoverProps {
  /** Full monthly consultation payload. */
  bundle: MonthlyConsultationBundle;
  /** 1-based sequential page index among rendered sheets. */
  pageNumber?: number;
}

/**
 * Resolve the webpack URL for the Canva cover celestial background.
 *
 * @returns Image URL, or empty string when the module did not yield a path
 */
function coverBackgroundSrc(): string {
  return typeof coverBackground === "string" ? coverBackground.trim() : "";
}

/**
 * Cover poster: cream top field, floating MONTHLY bar, REPORT, full-bleed
 * Canva art, month line, and matching gradient brand bar.
 *
 * The user-supplied `cover-background.png` already includes watercolor wash,
 * astrolabe wheel, planets, and orbits — do not stack `cover-astrolabe.png`.
 * Art aspect (~1:1.573) is taller than A4 (~1:1.414); `.mc-pdf-cover-bg`
 * uses `object-fit: cover` so Chromium crops top/bottom while filling width.
 * Shell `coverMode` removes the 14mm inset so bars hit the page edges.
 * Top padding leaves a cream/art strip above MONTHLY (Canva ~10–12% rhythm).
 */
export const PdfChapterCover: React.FC<PdfChapterCoverProps> = ({
  bundle,
  pageNumber,
}) => {
  const monthLine = monthPosterLabel(
    typeof bundle.monthLabel === "string" ? bundle.monthLabel : "",
    bundle.lunarMonth,
    bundle.solarYear
  );
  const background = coverBackgroundSrc();

  return (
    <PdfChapterShell
      chapterId="cover"
      eyebrow=""
      title=""
      pageNumber={pageNumber}
      coverMode={true}
    >
      <div
        className="mc-pdf-cover-poster"
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          alignSelf: "stretch",
          minHeight: "297mm",
          width: "100%",
          boxSizing: "border-box",
          backgroundColor: cream,
          /* Cream/art field above floating MONTHLY bar (Canva page-01). */
          paddingTop: "10%",
        }}
      >
        {background.length > 0 ? (
          <img
            className="mc-pdf-cover-bg"
            src={background}
            alt=""
            aria-hidden={true}
          />
        ) : null}

        {/* Floating top gradient bar — deepRed → coverOrange */}
        <div
          style={{
            position: "relative",
            zIndex: 1,
            flexShrink: 0,
            width: "100%",
            padding: "26px 24px",
            boxSizing: "border-box",
            background: coverBarGradient,
            textAlign: "center",
          }}
        >
          <p
            style={{
              margin: 0,
              fontFamily: coverCondensedFontFamily,
              fontSize: 38,
              fontWeight: 600,
              letterSpacing: "0.42em",
              textTransform: "uppercase",
              color: cardWhite,
              lineHeight: 1,
            }}
          >
            MONTHLY
          </p>
        </div>

        <p
          style={{
            position: "relative",
            zIndex: 1,
            margin: "32px 24px 0",
            fontFamily: coverCondensedFontFamily,
            fontSize: 86,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: charcoal,
            textAlign: "center",
            lineHeight: 0.9,
          }}
        >
          REPORT
        </p>

        {/* Flex spacer — celestial field shows through from the background img */}
        <div
          style={{
            position: "relative",
            zIndex: 1,
            flex: 1,
            minHeight: 280,
          }}
          aria-hidden={true}
        />

        <p
          style={{
            position: "relative",
            zIndex: 1,
            margin: "4px 24px 18px",
            fontFamily: coverCondensedFontFamily,
            fontSize: 26,
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: charcoal,
            textAlign: "center",
            lineHeight: 1,
          }}
        >
          {monthLine}
        </p>

        {/* Full-bleed brand bar — same gradient as MONTHLY; replaces default footer */}
        <div
          style={{
            position: "relative",
            zIndex: 1,
            flexShrink: 0,
            width: "100%",
            padding: "16px 24px",
            boxSizing: "border-box",
            background: coverBarGradient,
            textAlign: "center",
          }}
        >
          <p
            style={{
              margin: 0,
              fontFamily: coverCondensedFontFamily,
              fontSize: 14,
              fontWeight: 500,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: cardWhite,
              lineHeight: 1.2,
            }}
          >
            CAE GOH | PURPLE STAR ASTROLOGY
          </p>
        </div>
      </div>
    </PdfChapterShell>
  );
};

export default PdfChapterCover;
